/**
 * 激活页背景图双击进入拖拽移动模式（坐标相对 `data-page`）。
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import type { PageBackground } from '@/components/canvas/document/publicTypes';

interface UseBgImageDragOptions {
  background: PageBackground;
  /** 当前激活页的 `data-page` DOM */
  pageRef: React.RefObject<HTMLDivElement | null>;
  setPageBackground: (bg: PageBackground) => void;
}

export function useBgImageDrag({ background, pageRef, setPageBackground }: UseBgImageDragOptions) {
  const [isMovingBgImage, setIsMovingBgImage] = useState(false);
  const bgMoveStartRef = useRef({ clientX: 0, clientY: 0, posX: 50, posY: 50 });

  useEffect(() => {
    if (!isMovingBgImage || background.type !== 'image' || !background.value) return;
    const el = pageRef.current;
    if (!el) return;
    const handleMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const dx = ((e.clientX - bgMoveStartRef.current.clientX) / rect.width) * 100;
      const dy = ((e.clientY - bgMoveStartRef.current.clientY) / rect.height) * 100;
      const newX = Math.round(Math.max(0, Math.min(100, bgMoveStartRef.current.posX + dx)));
      const newY = Math.round(Math.max(0, Math.min(100, bgMoveStartRef.current.posY + dy)));
      setPageBackground({
        type: 'image',
        value: background.value,
        imagePositionX: newX,
        imagePositionY: newY,
        backgroundColor: background.backgroundColor ?? '#fff',
      });
    };
    const handleUp = () => {
      setIsMovingBgImage(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isMovingBgImage, background.type, background.value, background.backgroundColor, setPageBackground, pageRef]);

  const startBgImageDrag = useCallback(
    (e: React.MouseEvent) => {
      setIsMovingBgImage(true);
      bgMoveStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        posX: background.imagePositionX ?? 50,
        posY: background.imagePositionY ?? 50,
      };
    },
    [background.imagePositionX, background.imagePositionY]
  );

  return { isMovingBgImage, setIsMovingBgImage, startBgImageDrag };
}
